import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster, toast } from 'sonner';

import App from './App';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { WebSocketProvider } from './components/shared/WebSocketProvider';
import { initThemeListener, useThemeStore } from './lib/themeStore';
import { initPlatformTheme } from './lib/platformThemeStore';
import { registerServiceWorker } from './lib/push';
import { initErrorReporting } from './lib/errorReporter';
import { getApiErrorMessage, normaliseApiError } from './lib/api';
import './styles/globals.css';

// Register the service worker for browser push notifications (non-blocking, non-fatal).
void registerServiceWorker();

// Capture uncaught client errors / unhandled rejections for the platform diagnostics console.
initErrorReporting();

// Apply saved theme + wire OS change listener for System mode.
// The index.html inline script already set the class to prevent FOIC;
// this call re-confirms it and starts the matchMedia listener.
initThemeListener();

// The Platform Console keeps its own per-admin preference (PlatformAdmin has no UserPreference
// row). The inline script in index.html already painted; this starts the OS listener so
// "System" follows the OS live rather than only at reload.
if (window.location.pathname.startsWith('/platform')) {
  initPlatformTheme();
}

// Auto-reset corrupted localStorage on startup
try {
  const storeKeys = ['jurivoxa-matters', 'jurivoxa-hearings', 'jurivoxa-diary', 'jurivoxa-contacts', 'jurivoxa-bookings', 'jurivoxa-scopes', 'jurivoxa-conflicts', 'jurivoxa-notifications'];
  for (const key of storeKeys) {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      // If version is missing or old, clear it so the store reseeds
      if (!parsed.version || parsed.version < 2) {
        localStorage.removeItem(key);
      }
    }
  }
} catch {
  // If any parse fails, nuke everything
  const storeKeys = ['jurivoxa-auth', 'jurivoxa-matters', 'jurivoxa-hearings', 'jurivoxa-contacts', 'jurivoxa-diary', 'jurivoxa-bookings', 'jurivoxa-scopes', 'jurivoxa-conflicts', 'jurivoxa-notifications', 'jurivoxa-timer', 'jurivoxa-time-entries'];
  storeKeys.forEach((k) => localStorage.removeItem(k));
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      // Retry a FAULT once, never a REFUSAL. A 401/403/404 is a settled answer — the caller is
      // not permitted, or it is not there — and asking again returns the same thing. Retrying
      // them held the screen on a spinner while the outcome was already decided: a staff member
      // opening Time & Expenses (403 on every call) sat watching "Loading activities…" instead
      // of being told it is not available to them.
      retry: (failureCount, error) => {
        const status = normaliseApiError(error).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      onError: (error) => {
        // Global mutation error handler — show the server's reason ("Fee note is already paid"),
        // not axios's "Request failed with status code 409".
        toast.error(getApiErrorMessage(error, 'An error occurred'));
      },
    },
  },
});

// Toasts render through a portal, so they don't inherit the app's `.dark` class. Wire sonner's own
// theme to the theme store so neutral toasts (loading/message/mutation-error) render dark in dark
// mode instead of a white card. 'system' lets sonner follow the OS, matching the app's system mode.
function ThemedToaster() {
  const theme = useThemeStore((s) => s.theme);
  return <Toaster position="top-right" richColors theme={theme} />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <WebSocketProvider>
            <App />
            <ThemedToaster />
          </WebSocketProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
